import { useEffect, useRef, useState } from 'react';
import {
  listShares, removeGlobalShare, removeShare, searchUsers, shareSession, shareSessionWithAll,
  listGroups, listGroupShares, shareSessionWithGroup, removeGroupShare,
  getPublicLink, createPublicLink, deletePublicLink,
} from '../../services/api.js';
import styles from './ShareModal.module.css';

export default function ShareModal({ sessionId, sessionName, onClose, onShared, onUnshared }) {
  const [shareMode, setShareMode]   = useState('people'); // 'people' | 'group' | 'link'

  // link mode (account-free public share link)
  const [publicLink, setPublicLink]       = useState(null);   // { token, view_count, ... }
  const [linkLoaded, setLinkLoaded]       = useState(false);
  const [linkBusy, setLinkBusy]           = useState(false);
  const [linkError, setLinkError]         = useState('');
  const [copied, setCopied]               = useState(false);

  // people mode
  const [shares, setShares]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [permission, setPermission] = useState('view');
  const [adding, setAdding]         = useState(false);
  const [errors, setErrors]         = useState([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [removingId, setRemovingId] = useState(null);

  const [query, setQuery]           = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected]     = useState([]);
  const [shareAll, setShareAll]     = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // group mode
  const [groupOptions, setGroupOptions]     = useState([]);
  const [groupsLoaded, setGroupsLoaded]     = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupPermission, setGroupPermission] = useState('view');
  const [groupShares, setGroupShares]       = useState([]);
  const [groupSharesFetched, setGroupSharesFetched] = useState(false);
  const [loadingGroupShares, setLoadingGroupShares] = useState(false);
  const [addingGroup, setAddingGroup]       = useState(false);
  const [groupErrors, setGroupErrors]       = useState([]);
  const [removingGroupShareId, setRemovingGroupShareId] = useState(null);

  const searchRef   = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
    listShares(sessionId)
      .then(setShares)
      .catch(() => setShares([]))
      .finally(() => setLoading(false));
  }, [sessionId]);

  useEffect(() => {
    if (shareMode !== 'group') return;
    if (!groupsLoaded) {
      listGroups().then(data => {
        setGroupOptions(data);
        setGroupsLoaded(true);
        if (data.length > 0) setSelectedGroupId(data[0].id);
      }).catch(() => setGroupsLoaded(true));
    }
    if (!groupSharesFetched && !loadingGroupShares) {
      setLoadingGroupShares(true);
      listGroupShares(sessionId)
        .then(setGroupShares)
        .catch(() => setGroupShares([]))
        .finally(() => { setLoadingGroupShares(false); setGroupSharesFetched(true); });
    }
  }, [shareMode, groupsLoaded, groupSharesFetched, loadingGroupShares, sessionId]);

  useEffect(() => {
    if (shareMode !== 'link' || linkLoaded) return;
    getPublicLink(sessionId)
      .then(setPublicLink)
      .catch(() => setPublicLink(null))
      .finally(() => setLinkLoaded(true));
  }, [shareMode, linkLoaded, sessionId]);

  const linkUrl = publicLink ? `${window.location.origin}/s/${publicLink.token}` : '';

  async function handleCreateLink() {
    setLinkBusy(true);
    setLinkError('');
    try {
      const link = await createPublicLink(sessionId);
      setPublicLink(link);
    } catch (err) {
      let msg = err.message;
      try { msg = JSON.parse(msg).detail || msg; } catch {}
      setLinkError(msg);
    } finally {
      setLinkBusy(false);
    }
  }

  async function handleRevokeLink() {
    if (!window.confirm('Revoke this link? Anyone using it will lose access immediately.')) return;
    setLinkBusy(true);
    setLinkError('');
    try {
      await deletePublicLink(sessionId);
      setPublicLink(null);
    } catch (err) {
      setLinkError(err.message);
    } finally {
      setLinkBusy(false);
    }
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable; the input is selectable as a fallback */
    }
  }

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (shareAll) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      if (query.length < 2) { setSuggestions([]); return; }
      try {
        const results = await searchUsers(query);
        const selectedIds  = new Set(selected.map(u => u.id));
        const sharedEmails = new Set(shares.map(s => s.shared_with_email).filter(Boolean));
        setSuggestions(results.filter(u => !selectedIds.has(u.id) && !sharedEmails.has(u.email)));
      } catch {
        setSuggestions([]);
      }
    }, 200);
  }, [query, selected, shares, shareAll]);

  function selectUser(user) {
    setShareAll(false);
    setSelected(prev => [...prev, user]);
    setQuery('');
    setSuggestions([]);
    setDropdownOpen(false);
    searchRef.current?.focus();
  }

  function selectAllUsers() {
    setSelected([]);
    setShareAll(true);
    setQuery('');
    setSuggestions([]);
    setDropdownOpen(false);
  }

  function removeSelected(userId) {
    setSelected(prev => prev.filter(u => u.id !== userId));
  }

  function closeWithMessage(msg) {
    setSuccessMsg(msg);
    onShared?.();
    setTimeout(onClose, 1500);
  }

  async function handleShare(e) {
    e.preventDefault();
    if (!shareAll && selected.length === 0) return;
    setErrors([]);
    setAdding(true);

    if (shareAll) {
      try {
        const result = await shareSessionWithAll(sessionId, permission);
        const msg = result.permission
          ? `Shared with all users (${result.permission})`
          : 'Shared with all users';
        closeWithMessage(msg);
        setShareAll(false);
      } catch (err) {
        let msg = err.message;
        try { msg = JSON.parse(msg).detail || msg; } catch {}
        setErrors([msg]);
      }
    } else {
      const errs = [];
      const added = [];
      for (const user of selected) {
        try {
          const share = await shareSession(sessionId, user.email, permission);
          added.push(share);
        } catch (err) {
          let msg = err.message;
          try { msg = JSON.parse(msg).detail || msg; } catch {}
          errs.push(`${user.email}: ${msg}`);
        }
      }
      if (added.length > 0) {
        setShares(prev => [...prev, ...added]);
        setSelected([]);
        if (errs.length === 0) {
          const names = added.map(s => s.shared_with_display_name || s.shared_with_email).join(', ');
          closeWithMessage(`Shared with ${names}`);
        }
      }
      if (errs.length > 0) setErrors(errs);
    }
    setAdding(false);
  }

  async function handleRemove(share) {
    setRemovingId(share.id);
    try {
      if (share.is_global) {
        await removeGlobalShare(sessionId);
      } else {
        await removeShare(sessionId, share.id);
      }
      setShares(prev => prev.filter(s => s.id !== share.id));
      onUnshared?.();
    } catch (err) {
      alert(`Failed to remove share: ${err.message}`);
    } finally {
      setRemovingId(null);
    }
  }

  async function handleGroupShare(e) {
    e.preventDefault();
    if (!selectedGroupId) return;
    setGroupErrors([]);
    setAddingGroup(true);
    try {
      const share = await shareSessionWithGroup(sessionId, selectedGroupId, groupPermission);
      setGroupShares(prev => {
        const filtered = prev.filter(s => s.group_id !== share.group_id);
        return [...filtered, share];
      });
      onShared?.();
    } catch (err) {
      let msg = err.message;
      try { msg = JSON.parse(msg).detail || msg; } catch {}
      setGroupErrors([msg]);
    } finally {
      setAddingGroup(false);
    }
  }

  async function handleRemoveGroupShare(share) {
    setRemovingGroupShareId(share.id);
    try {
      await removeGroupShare(sessionId, share.group_id);
      setGroupShares(prev => prev.filter(s => s.id !== share.id));
      onUnshared?.();
    } catch (err) {
      alert(`Failed to remove group share: ${err.message}`);
    } finally {
      setRemovingGroupShareId(null);
    }
  }

  const hasRecipient  = shareAll || selected.length > 0;
  const showDropdown  = dropdownOpen && !shareAll;

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Share session</h2>
            <p className={styles.subtitle}>{sessionName || sessionId}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        {successMsg ? (
          <p className={styles.successMsg}>{successMsg}</p>
        ) : (
          <>
            <div className={styles.modeToggle}>
              <button
                type="button"
                className={`${styles.modeBtn} ${shareMode === 'people' ? styles.modeBtnActive : ''}`}
                onClick={() => setShareMode('people')}
              >
                People
              </button>
              <button
                type="button"
                className={`${styles.modeBtn} ${shareMode === 'group' ? styles.modeBtnActive : ''}`}
                onClick={() => setShareMode('group')}
              >
                Groups
              </button>
              <button
                type="button"
                className={`${styles.modeBtn} ${shareMode === 'link' ? styles.modeBtnActive : ''}`}
                onClick={() => setShareMode('link')}
              >
                Link
              </button>
            </div>

            {shareMode === 'link' && (
              <div className={styles.shareList}>
                <p className={styles.empty} style={{ marginBottom: 8 }}>
                  Anyone with this link can view this session read-only — no account needed.
                </p>
                {!linkLoaded && <p className={styles.empty}>Loading…</p>}
                {linkLoaded && !publicLink && (
                  <button
                    className={styles.shareBtn}
                    type="button"
                    onClick={handleCreateLink}
                    disabled={linkBusy}
                  >
                    {linkBusy ? '…' : 'Create link'}
                  </button>
                )}
                {linkLoaded && publicLink && (
                  <>
                    <div className={styles.addRow}>
                      <input
                        className={styles.searchInput}
                        type="text"
                        readOnly
                        value={linkUrl}
                        onFocus={e => e.target.select()}
                        style={{ flex: 1, minWidth: 0 }}
                      />
                      <button className={styles.shareBtn} type="button" onClick={handleCopyLink}>
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className={styles.shareRow}>
                      <div className={styles.shareInfo}>
                        <span className={styles.shareName}>
                          {publicLink.view_count} view{publicLink.view_count === 1 ? '' : 's'}
                        </span>
                      </div>
                      <button
                        className={styles.removeBtn}
                        type="button"
                        onClick={handleRevokeLink}
                        disabled={linkBusy}
                        title="Revoke this link"
                      >
                        {linkBusy ? '…' : 'Revoke'}
                      </button>
                    </div>
                  </>
                )}
                {linkError && <p className={styles.error}>{linkError}</p>}
              </div>
            )}

            {shareMode === 'group' && (
              <>
                <form onSubmit={handleGroupShare}>
                  <div className={styles.addRow}>
                    <select
                      className={`${styles.permSelect} ${styles.groupSelect}`}
                      value={selectedGroupId}
                      onChange={e => setSelectedGroupId(e.target.value)}
                      disabled={groupOptions.length === 0}
                    >
                      {groupOptions.length === 0
                        ? <option value="">No groups yet</option>
                        : groupOptions.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))
                      }
                    </select>
                    <select
                      className={styles.permSelect}
                      value={groupPermission}
                      onChange={e => setGroupPermission(e.target.value)}
                    >
                      <option value="view">View</option>
                      <option value="comment">Comment</option>
                    </select>
                    <button
                      className={styles.shareBtn}
                      type="submit"
                      disabled={addingGroup || !selectedGroupId || groupOptions.length === 0}
                    >
                      {addingGroup ? '…' : 'Share'}
                    </button>
                  </div>
                </form>

                {groupErrors.length > 0 && (
                  <div className={styles.errorList}>
                    {groupErrors.map((e, i) => <p key={i} className={styles.error}>{e}</p>)}
                  </div>
                )}

                <div className={styles.shareList}>
                  {loadingGroupShares && <p className={styles.empty}>Loading…</p>}
                  {!loadingGroupShares && groupShares.length === 0 && (
                    <p className={styles.empty}>Not shared with any groups yet.</p>
                  )}
                  {groupShares.map(s => (
                    <div key={s.id} className={styles.shareRow}>
                      <div className={styles.shareInfo}>
                        <span className={styles.shareEmail}>{s.group_name}</span>
                        <span className={styles.shareName}>group</span>
                      </div>
                      <span className={styles.permBadge}>{s.permission}</span>
                      <button
                        className={styles.removeBtn}
                        onClick={() => handleRemoveGroupShare(s)}
                        disabled={removingGroupShareId === s.id}
                        title="Remove group access"
                      >
                        {removingGroupShareId === s.id ? '…' : 'Unshare'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {shareMode === 'people' && <><form onSubmit={handleShare}>
              <div className={styles.searchWrapper}>
                <div
                  className={styles.chipInput}
                  onClick={() => !shareAll && searchRef.current?.focus()}
                >
                  {shareAll ? (
                    <span className={`${styles.chip} ${styles.chipAll}`}>
                      <span className={styles.chipLabel}>All users</span>
                      <button
                        type="button"
                        className={styles.chipRemove}
                        onClick={() => setShareAll(false)}
                        aria-label="Remove All users"
                      >×</button>
                    </span>
                  ) : (
                    <>
                      {selected.map(u => (
                        <span key={u.id} className={styles.chip}>
                          <span className={styles.chipLabel}>{u.display_name || u.email}</span>
                          <button
                            type="button"
                            className={styles.chipRemove}
                            onClick={() => removeSelected(u.id)}
                            aria-label={`Remove ${u.email}`}
                          >×</button>
                        </span>
                      ))}
                      <input
                        ref={searchRef}
                        className={styles.searchInput}
                        type="text"
                        placeholder={selected.length === 0 ? 'Search by name or email…' : ''}
                        value={query}
                        onChange={e => { setQuery(e.target.value); setErrors([]); }}
                        onFocus={() => setDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                        autoComplete="off"
                      />
                    </>
                  )}
                </div>

                {showDropdown && (
                  <ul className={styles.dropdown} role="listbox">
                    <li
                      className={`${styles.dropdownItem} ${styles.dropdownAllUsers}`}
                      role="option"
                      onMouseDown={selectAllUsers}
                    >
                      <span className={styles.dropdownName}>🏆 All users (public leaderboard)</span>
                      <span className={styles.dropdownEmail}>Everyone can see this & compare on the leaderboard</span>
                    </li>
                    {suggestions.map(u => (
                      <li
                        key={u.id}
                        className={styles.dropdownItem}
                        role="option"
                        onMouseDown={() => selectUser(u)}
                      >
                        <span className={styles.dropdownName}>{u.display_name || u.email}</span>
                        {u.display_name && (
                          <span className={styles.dropdownEmail}>{u.email}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {shareAll ? (
                <p className={styles.leaderboardNote}>
                  🏆 Everyone on Track Analysis will see this session and can compare against it on the public leaderboard.
                </p>
              ) : selected.length > 0 ? (
                <p className={styles.leaderboardNote}>
                  Only the people above will see this on their leaderboard. To appear on the <strong>public</strong> leaderboard, choose <strong>All users</strong>.
                </p>
              ) : (
                <p className={styles.leaderboardNoteDim}>
                  Tip: to appear on the public leaderboard, share with <strong>All users</strong>. Sharing with specific people keeps it visible only to them.
                </p>
              )}

              <div className={styles.addRow}>
                <select
                  className={styles.permSelect}
                  value={permission}
                  onChange={e => setPermission(e.target.value)}
                >
                  <option value="view">View</option>
                  <option value="comment">Comment</option>
                </select>
                <button
                  className={styles.shareBtn}
                  type="submit"
                  disabled={adding || !hasRecipient}
                >
                  {adding ? '…' : 'Share'}
                </button>
              </div>
            </form>

            {errors.length > 0 && (
              <div className={styles.errorList}>
                {errors.map((e, i) => <p key={i} className={styles.error}>{e}</p>)}
              </div>
            )}

            <div className={styles.shareList}>
              {loading && <p className={styles.empty}>Loading…</p>}
              {!loading && shares.length === 0 && (
                <p className={styles.empty}>Not shared with anyone yet.</p>
              )}
              {shares.map(s => (
                <div key={s.id} className={`${styles.shareRow}${s.is_global ? ` ${styles.shareRowGlobal}` : ''}`}>
                  <div className={styles.shareInfo}>
                    {s.is_global ? (
                      <span className={styles.shareEmail}>All users</span>
                    ) : (
                      <>
                        <span className={styles.shareEmail}>{s.shared_with_email}</span>
                        {s.shared_with_display_name && (
                          <span className={styles.shareName}>{s.shared_with_display_name}</span>
                        )}
                      </>
                    )}
                  </div>
                  <span className={styles.permBadge}>{s.permission}</span>
                  <button
                    className={styles.removeBtn}
                    onClick={() => handleRemove(s)}
                    disabled={removingId === s.id}
                    title="Remove access"
                  >
                    {removingId === s.id ? '…' : 'Unshare'}
                  </button>
                </div>
              ))}
            </div>
            </>}
          </>
        )}
      </div>
    </div>
  );
}
